import { ActionsUnion, createAction } from '@housinganywhere/safe-redux';
import HttpStatus from 'ha/constants/HttpStatus';
import { ErrorMessages } from 'ha/constants/ErrorMessages';
import { removeFromStorage } from 'ha/utils/storage';
import { MakeCallPromise } from 'ha/services/apiV2/createMakeCall';
import { Thunk } from 'ha/myredux/types';
import { User } from 'ha/services/apiV2/types/User';
import {
  ImpersonationInfo,
  MyUserInfoApiV2,
} from 'ha/services/apiV2/types/UserInformation';
import { Payload as LoginPayload } from 'ha/services/apiV2/login';
import { Payload as RegisterPayload } from 'ha/services/apiV2/register';
import { changeLocation } from 'ha/modules/URLLocationHandler';
import {
  BILLING_INFO_STORAGE_KEY,
  SHOW_WE_WANT_TO_PAY_STORAGE_KEY,
} from 'ha/modules/WeWantToPay/constants';
import { AuthLogicTypes } from 'ha/modules/AuthLogic/constants';
import { RequestError } from 'ha/services/apiV2';
import { UpdateUserPayload } from 'ha/services/apiV2/updateUser';
import { AuthType } from '../types';
import fetchUserBillingInfo from './fetchUserBillingInfo';

export const AuthLogicActions = {
  authStart: () => createAction(AuthLogicTypes.authStart),
  authDone: () => createAction(AuthLogicTypes.authDone),
  authFail: () => createAction(AuthLogicTypes.authFail),
  login: (user: User, userInformation: MyUserInfoApiV2, dialogType: AuthType) =>
    createAction(
      AuthLogicTypes.login,
      { user, userInformation },
      { analytics: { type: dialogType, payload: { user } } },
    ),
  logoutStart: () =>
    createAction(AuthLogicTypes.logoutStart, undefined, {
      analytics: { type: 'logout' },
    }),
  logoutDone: () => createAction(AuthLogicTypes.logoutDone),
  updateUser: (payload: Partial<User>) =>
    createAction(AuthLogicTypes.updateUser, payload),
  initUser: (payload: { user: User; userInformation: MyUserInfoApiV2 }) =>
    createAction(AuthLogicTypes.initUser, payload),
  fulfillImpersonation: (payload: ImpersonationInfo) =>
    createAction(AuthLogicTypes.fulfillImpersonation, payload),
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AuthLogicActions = ActionsUnion<typeof AuthLogicActions>;

export const updateUserData = AuthLogicActions.updateUser;

export const login: (user: User, dialogType: AuthType) => Thunk =
  (user, dialogType) => (dispatch, _, services) =>
    services.apiV2
      .getUserInformation('me', {
        expand:
          'advertiserBookingsCount,savedPaymentMethodsCount,listingsCount,subscriptionFeatures',
      })
      .then(({ data: userInformation }) => {
        dispatch(AuthLogicActions.login(user, userInformation, dialogType));
        removeFromStorage(BILLING_INFO_STORAGE_KEY);
        removeFromStorage(SHOW_WE_WANT_TO_PAY_STORAGE_KEY);
        // do not return this to avoid making form submission invalid if
        // fetching user information fails (which is just to update banner)
        dispatch(fetchUserBillingInfo());
      });

type LogoutReturn = MakeCallPromise<null>;

export const logout: (nextUrl?: string) => Thunk<{}, LogoutReturn> =
  nextUrl =>
  (dispatch, _, { apiV2 }) => {
    dispatch(AuthLogicActions.logoutStart());

    removeFromStorage(BILLING_INFO_STORAGE_KEY);
    removeFromStorage(SHOW_WE_WANT_TO_PAY_STORAGE_KEY);

    const resolvedPromise = apiV2.logout().then(data => {
      if (nextUrl) {
        // if redirect is happening, avoid unnecessary page rerendering by modifying auth state
        // it can also lead to error in firefox
        // (network requests done during redirect are aborted with an error, breaking loadable components)

        dispatch(changeLocation(nextUrl));

        return data;
      }

      dispatch(AuthLogicActions.logoutDone());

      return data;
    });

    return resolvedPromise;
  };

type LoginRequest = (fields: LoginPayload, nextUrl?: string) => Thunk;

export const loginRequest: LoginRequest =
  (fields, nextUrl) => (dispatch, getState, services) => {
    dispatch(AuthLogicActions.authStart());

    return services.apiV2
      .login(fields)
      .then(({ data }) => {
        if (nextUrl) {
          // if redirect is happening, avoid unnecessary page rerendering by modifying auth state
          // it can also lead to error in firefox
          // (network requests done during redirect are aborted with an error, breaking loadable components)
          return dispatch(changeLocation(nextUrl));
        }

        dispatch(AuthLogicActions.authDone());

        return dispatch(login(data, AuthType.login));
      })
      .catch((err: RequestError) => {
        dispatch(AuthLogicActions.authFail());

        if (err.status === HttpStatus.FORBIDDEN) {
          throw new Error(ErrorMessages.emailOrPasswordIncorrect);
        } else if (err.status === HttpStatus.SERVICE_UNAVAILABLE) {
          throw new Error(ErrorMessages.maxAttemptsReached);
        }

        throw err;
      });
  };

type RegisterRequest = (fields: RegisterPayload, nextUrl?: string) => Thunk;

export const registerRequest: RegisterRequest =
  (fields, nextUrl) => (dispatch, getState, services) => {
    dispatch(AuthLogicActions.authStart());

    return services.apiV2
      .register(fields)
      .then(({ data }) => {
        if (nextUrl) {
          // if redirect is happening, avoid unnecessary page rerendering by modifying auth state
          // it can also lead to error in firefox
          // (network requests done during redirect are aborted with an error, breaking loadable components)
          return dispatch(changeLocation(nextUrl));
        }

        dispatch(AuthLogicActions.authDone());

        return dispatch(login(data, AuthType.register));
      })
      .catch((err: RequestError) => {
        dispatch(AuthLogicActions.authFail());

        throw err;
      });
  };

export const fetchUser: () => Thunk = () => (dispatch, getState, services) =>
  Promise.all([
    services.apiV2.getUser('me'),
    services.apiV2.getUserInformation('me', {
      expand:
        'advertiserBookingsCount,savedPaymentMethodsCount,listingsCount,subscriptionFeatures',
    }),
  ])
    .then(([{ data: user }, { data: userInformation }]) => {
      dispatch(
        AuthLogicActions.initUser({
          user,
          userInformation,
        }),
      );
    })
    .catch(() => {
      dispatch(AuthLogicActions.authDone());
    });

export const fetchImpersonationInfo: () => Thunk =
  () =>
  (dispatch, _, { apiV2 }) =>
    apiV2
      .getUserInformation('me', {
        expand: 'impersonation',
      })
      .then(({ data: { impersonation } }) => {
        if (impersonation) {
          dispatch(AuthLogicActions.fulfillImpersonation(impersonation));

          removeFromStorage(BILLING_INFO_STORAGE_KEY);
          removeFromStorage(SHOW_WE_WANT_TO_PAY_STORAGE_KEY);
        }
      })
      .catch(() => {
        // do nothing
      });

export const updateUser: (id: number, newValues: UpdateUserPayload) => Thunk =
  (userId, newValues) =>
  (dispatch, _, { apiV2 }) =>
    apiV2.updateUser(userId, newValues).then(newUser => {
      const userData = newUser.data as Partial<User>;

      delete userData.avatarURL;

      return dispatch(AuthLogicActions.updateUser(userData));
    });
